import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Navbar from '../common/Navbar';
import AssignmentList from './AssignmentList';
import QuestionList from './QuestionList';
import QuestionView from './QuestionView';
import NotificationPermissionBanner from './NotificationPermissionBanner';
import {
  getNotificationPermission,
  requestNotificationPermission,
} from '../../utils/timeoutNotifications';

let notificationPermissionAutoRequestStarted = false;

const CandidateDashboard = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState('assignments'); // 'assignments', 'questions', 'question'
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [timePerQuestionMinutes, setTimePerQuestionMinutes] = useState(15);
  const [notificationPermission, setNotificationPermission] = useState(
    getNotificationPermission()
  );

  useEffect(() => {
    if (notificationPermissionAutoRequestStarted) return;
    if (getNotificationPermission() !== 'default') return;

    notificationPermissionAutoRequestStarted = true;
    requestNotificationPermission().then(setNotificationPermission);
  }, []);

  // Re-sync the permission state when the candidate comes back to the tab,
  // in case they changed it from the browser's site settings.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setNotificationPermission(getNotificationPermission());
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const handleRequestNotifications = async () => {
    const result = await requestNotificationPermission();
    setNotificationPermission(result);
  };

  const handleStartTest = (assignment) => {
    setSelectedAssignment(assignment);
    setCurrentView('questions');
  };

  const handleStartQuestion = (question, timeMinutes) => {
    setSelectedQuestion(question);
    if (typeof timeMinutes === 'number') {
      setTimePerQuestionMinutes(timeMinutes);
    }
    setCurrentView('question');
  };

  const handleQuestionComplete = () => {
    // Go back to questions list
    setCurrentView('questions');
    setSelectedQuestion(null);
  };

  const handleBackToAssignments = () => {
    setCurrentView('assignments');
    setSelectedAssignment(null);
    setSelectedQuestion(null);
  };

  const handleBackToQuestions = () => {
    setCurrentView('questions');
    setSelectedQuestion(null);
  };

  // The banner is most useful before the candidate starts a question.
  // Once they're actively recording, we don't want to distract them.
  const showBanner = currentView !== 'question';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Navbar user={user} onLogout={onLogout} />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom color="primary">
          {t('candidate.dashboard')}
        </Typography>

        {showBanner && (
          <NotificationPermissionBanner
            permission={notificationPermission}
            onRequest={handleRequestNotifications}
          />
        )}

        {/* Assignments View */}
        {currentView === 'assignments' && (
          <AssignmentList user={user} onStartTest={handleStartTest} />
        )}

        {/* Questions View */}
        {currentView === 'questions' && selectedAssignment && (
          <QuestionList
            assignment={selectedAssignment}
            onStartQuestion={handleStartQuestion}
            onBack={handleBackToAssignments}
          />
        )}

        {/* Question View */}
        {currentView === 'question' && selectedQuestion && selectedAssignment && (
          <QuestionView
            assignment={selectedAssignment}
            question={selectedQuestion}
            timePerQuestionMinutes={timePerQuestionMinutes}
            onComplete={handleQuestionComplete}
            onBack={handleBackToQuestions}
          />
        )}
      </Container>
    </Box>
  );
};

export default CandidateDashboard;
